import React, { useState, useEffect } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import FormResponses from './FormResponses';
import { useSnackbar } from 'notistack';
import AppLoader from '../../shared/AppLoader';
import useAPI from '../../utils/api';
import Button from '../../shared/Button';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Switch from '@mui/material/Switch';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import ConfirmationDialog from '../Modals/ConfirmationDialog';

const FORM_TYPE_LABELS = {
  survey:   'Survey',
  poll:     'Poll',
  lead_gen: 'Lead Generation',
  feedback: 'Feedback',
  quiz:     'Quiz',
  contact:  'Contact Form',
  crm:      'CRM Inquiry',
};

export default function Forms() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedForm, setSelectedForm] = useState(null);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState('30');
  const API = useAPI();
  const { siteId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const getDateParams = (range) => {
    const to = new Date().toISOString().split('T')[0];
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - parseInt(range));
    const from = fromDate.toISOString().split('T')[0];
    return { from, to };
  };

  useEffect(() => {
    if (location.state?.success) {
      enqueueSnackbar(location.state.message || 'Success!', { variant: 'success' });
      history.replace({ ...location, state: undefined });
    }
  }, [location, enqueueSnackbar, history]);

  useEffect(() => {
    let cancelled = false;

    const fetchForms = async () => {
      setLoading(true);
      try {
        const [ok, data] = await API.formsList();
        if (cancelled) return;
        if (ok && data) {
          const list = Array.isArray(data) ? data : (data.site_elements || []);
          setForms(list);

          const { from, to } = getDateParams(dateRange);
          list.forEach(async (form) => {
            const id = String(form.id);
            const [statsOk, statsData] = await API.popupGraphSummary(id, from, to);
            if (!cancelled && statsOk && statsData?.numbers) {
              setStats(prev => ({ ...prev, [id]: statsData.numbers }));
            }
          });
        } else {
          setForms([]);
        }
      } catch (err) {
        if (!cancelled) console.error('Error fetching forms', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchForms();

    return () => { cancelled = true; };
  }, [siteId, dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredForms = forms.filter(f => {
    const matchSearch = (f.name || f.headline || '').toLowerCase().includes(search.toLowerCase());
    if (filter === 'active') return matchSearch && !f.paused;
    if (filter === 'paused') return matchSearch && !!f.paused;
    return matchSearch;
  });

  const toggleForm = async (form) => {
    try {
      const [ok] = await API.formTogglePause(form.id);
      if (ok) {
        setForms(prev => prev.map(f =>
          f.id === form.id ? { ...f, paused: !f.paused } : f
        ));
        try {
          window.analytics?.track(form.paused ? 'Form Activated' : 'Form Paused', {
            form_id: form.id,
            site_id: siteId,
          });
        } catch (_) {}
      }
    } catch (err) {
      console.error('Error toggling form', err);
    }
  };

  const deleteForm = async (form) => {
    try {
      const [ok] = await API.formDelete(form.id);
      if (ok) {
        setForms(prev => prev.filter(f => f.id !== form.id));
        enqueueSnackbar('Form deleted.', { variant: 'success' });
        try {
          window.analytics?.track('Form Deleted', { form_id: form.id, site_id: siteId });
        } catch (_) {}
      }
    } catch (err) {
      console.error('Error deleting form', err);
    }
  };

  if (loading) return <AppLoader visible={true} />;

  if (selectedForm) {
    return <FormResponses form={selectedForm} onBack={() => setSelectedForm(null)} siteId={siteId} />;
  }

  return (
    <div style={{ backgroundColor: '#F2F1F1', minHeight: '100vh', padding: '0px' }}>
      {/* Header */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '20px',
      }}>
        <div>
          <h1 style={{ color: '#23262A', fontWeight: 'bold', fontSize: '28px', margin: 0, lineHeight: '1.2' }}>
            Forms &amp; Surveys
          </h1>
          <p style={{ color: '#777', fontSize: '15px', margin: '8px 0 0 0', maxWidth: '500px' }}>
            Manage your forms and surveys and track how many visitors are responding.
          </p>
        </div>
        <Button
          variant="contained"
          onClick={() => history.push(`/sites/${siteId}/forms/new/interstitial/goal`)}
          label="+ New Form"
          style={{ padding: '12px 24px', fontSize: '15px', fontWeight: 'bold', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}
        />
      </div>

      {/* Filter bar */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto 20px auto',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: '#fff',
        padding: '10px 20px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
        border: '1px solid #E5E7EB',
      }}>
        <Tabs
          value={filter}
          onChange={(e, val) => setFilter(val)}
          textColor="primary"
          indicatorColor="primary"
          sx={{ '& .MuiTab-root': { fontSize: '14px', fontWeight: 'bold', minWidth: '80px' } }}
        >
          <Tab value="all" label="All" />
          <Tab value="active" label="Active" />
          <Tab value="paused" label="Paused" />
        </Tabs>

        <TextField
          size="small"
          placeholder="Search forms..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: '250px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <CalendarTodayIcon style={{ color: '#6B7280', fontSize: '18px' }} />
          <Select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            size="small"
            sx={{ width: '160px', fontSize: '13px', fontWeight: '600', '& .MuiSelect-select': { paddingY: '8.5px' } }}
          >
            <MenuItem value="0">Today</MenuItem>
            <MenuItem value="7">Last 7 Days</MenuItem>
            <MenuItem value="30">Last 30 Days</MenuItem>
            <MenuItem value="90">Last 90 Days</MenuItem>
          </Select>
        </div>
      </div>

      {/* List */}
      {filteredForms.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '80px 40px',
          background: '#fff',
          borderRadius: '12px',
          boxShadow: '0 2px 15px rgba(0,0,0,0.05)',
          border: '1px solid #e0e0e0',
          maxWidth: '1200px',
          margin: '0 auto',
        }}>
          <div style={{ fontSize: '50px', marginBottom: '20px' }}>{search ? '🔍' : '📋'}</div>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '22px', color: '#23262A', fontWeight: 'bold' }}>
            {search ? 'No forms match your search' : 'No forms found'}
          </h3>
          <p style={{ margin: '0 0 25px 0', color: '#777', fontSize: '16px' }}>
            {search
              ? 'Try a different search term or check your filters.'
              : 'Create your first form to start collecting responses from your visitors.'}
          </p>
          {!search && (
            <Button
              variant="contained"
              onClick={() => history.push(`/sites/${siteId}/forms/new/interstitial/goal`)}
              label="+ New Form"
              style={{ padding: '10px 30px' }}
            />
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '1200px', margin: '0 auto' }}>
          {filteredForms.map(form => {
            const isActive = !form.paused;
            const formTypeLabel = FORM_TYPE_LABELS[form.form_type] || form.form_type || 'Form';
            const fieldCount = form.form_questions?.length || 0;
            const formStats = stats[String(form.id)];
            const views = formStats?.total_humanized || '0';
            const submissions = formStats?.conversion_humanized || '0';
            const ctr = formStats
              ? ((parseInt(formStats.conversion) / Math.max(parseInt(formStats.total), 1)) * 100).toFixed(1)
              : '0.0';

            return (
              <div key={form.id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '25px',
                background: '#fff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                borderRadius: '10px',
                border: '1px solid #efefef',
              }}>
                {/* Left: info */}
                <div>
                  <strong style={{ fontSize: '18px', color: '#23262A' }}>
                    {form.name || form.headline || 'Untitled Form'}
                  </strong>
                  <div style={{ marginTop: '5px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{
                      fontSize: '11px',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      fontWeight: 'bold',
                      padding: '4px 10px',
                      borderRadius: '20px',
                      border: '1px solid #D1D9E0',
                      background: '#F0F4F8',
                      color: '#476684',
                    }}>
                      {formTypeLabel}
                    </span>
                    <span style={{ fontSize: '13px', color: '#888' }}>
                      Type: <strong>{form.type}</strong>
                    </span>
                    {fieldCount > 0 && (
                      <span style={{ fontSize: '13px', color: '#888' }}>
                        Fields: <strong>{fieldCount}</strong>
                      </span>
                    )}
                  </div>
                </div>

                {/* Right: stats + actions */}
                <div style={{ display: 'flex', gap: '25px', alignItems: 'center' }}>
                  <div style={{ textAlign: 'center', minWidth: '70px' }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#23262A' }}>{views}</div>
                    <div style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: '0.5px' }}>Views</div>
                  </div>

                  <div
                    style={{ textAlign: 'center', minWidth: '70px', cursor: 'pointer' }}
                    onClick={() => setSelectedForm(form)}
                  >
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#ED5A29', textDecoration: 'underline' }}>{submissions}</div>
                    <div style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: '0.5px' }}>Submissions</div>
                  </div>

                  <div style={{ textAlign: 'center', minWidth: '70px' }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#ED5A29' }}>{ctr}%</div>
                    <div style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: '0.5px' }}>Conv. Rate</div>
                  </div>

                  <div style={{ width: '1px', height: '30px', background: '#EEE', margin: '0 5px' }} />

                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Tooltip title={isActive ? 'Pause Form' : 'Activate Form'}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Switch
                          checked={isActive}
                          onChange={() => toggleForm(form)}
                          color="primary"
                          size="small"
                        />
                      </div>
                    </Tooltip>

                    <div style={{ width: '10px' }} />

                    <Tooltip title="Edit Form">
                      <IconButton
                        onClick={() => {
                          try {
                            window.analytics?.track('Form Edited', { form_id: form.id, site_id: siteId });
                          } catch (_) {}
                          history.push(`/sites/${siteId}/forms/${form.id}/design`);
                        }}
                        size="small"
                        style={{ color: '#ED5A29', background: '#FFF0EB' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <ConfirmationDialog
                      onConfirm={() => deleteForm(form)}
                      component={(props) => (
                        <Tooltip title="Delete Form">
                          <IconButton
                            {...props}
                            size="small"
                            style={{ color: '#d32f2f', background: '#FFEBEE' }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    >
                      Are you sure you want to delete this form? This action cannot be undone.
                    </ConfirmationDialog>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
